import { BindAll } from 'lodash-decorators'
import { action, computed, observable } from 'mobx'
import { reportEntryLog } from '@xt/client/utils/log'
import { $service } from '@xt/client/experimental/request/service'
import moment from 'moment'
import { EnumApiMy, EnumApiUser, EnumApiProduct } from '../../api'
import type { CourseCardUIType } from './types.d'
import type { CourseItem, MyCourseList, ICalendarData } from '@xt/client/types/my-go-course'
import type { MyClassroomType, StudyReviewType, UserStatisticsResponse, CalendarTimeData } from '@xt/client/entities/my-v2/types'
import { transfromProductList, formatYearMonthDay } from '@xt/client/utils/my'
import { AjaxBasics } from '@xt/client/helpers/ajaxBasics'
import lodash from 'lodash'

type MyProductStatus = {
  loading: boolean
  isError: boolean
  haveNoneCourse: boolean
}

@BindAll()
export class MyClassroomController {
  @observable userPanelData: MyClassroomType['UserPanel'] = {
    follow: 0,
    followed: 0,
    like: 0
  }
  @observable studyRecordData: MyClassroomType['StudyRecord'] | null = null
  @observable myProductList: Array<CourseItem & CourseCardUIType> = []
  @observable myProductStatus: MyProductStatus = {
    loading: true,
    haveNoneCourse: false,
    isError: false
  }
  @observable reviewList: Array<StudyReviewType> = []
  @observable studyActionData: MyClassroomType['StudyAction'] = {
    homework: 0,
    rewards: 0,
    daily: 0,
    collect: 0
  }

  @observable calendarTimeData: Array<CalendarTimeData> = []
  @observable allClasshourList: Array<
    ICalendarData & {
      y: number
      m: number
      d: number
    }
  > = []

  @computed
  get dataList() {
    return lodash.chunk(this.calendarTimeData, 7)
  }

  singlePadding(num) {
    return num <= 9 ? '0' + num : num
  }

  get21DayCalendarData() {
    let week = AjaxBasics.serviceDate.utcOffset(+8).startOf('isoWeek')
    let dayTime = 24 * 1000 * 60 * 60
    let time = week.subtract(7, 'days').valueOf()
    let currWeek: any[] = []
    for (let i = 0; i < 21; i++) {
      let item = moment(time)
      const [y, m, d] = item
        .format('YYYY-MM-DD')
        .split('-')
        .map(v => parseInt(v))
      currWeek.push({ w: item.day(), y, m, d, timestamp: item.valueOf() })
      time += dayTime
    }
    this.setCalendarTimeData(currWeek)
  }

  async loadClasshourList() {
    const { y, m, d } = this.calendarTimeData[0]
    const [, res] = await $service<Array<ICalendarData>>({
      url: EnumApiProduct.productCalendar,
      method: 'post',
      data: {
        startTime: moment(`${this.singlePadding(y)}-${this.singlePadding(m)}-${this.singlePadding(d)}`).valueOf(),
        countDay: 21
      }
    })
    // 数据处理
    let list = (res ?? []).map(i => {
      const { y, m, d } = formatYearMonthDay(i.startTime)
      return {
        ...i,
        y,
        m,
        d
      }
    })
    this.setAllClasshourList(list)
  }

  // 初始化日历模块数据
  async initCalendarData() {
    // 获取21天日历数据
    this.get21DayCalendarData()
    // 初始化课时数据
    await this.loadClasshourList()
  }

  // 获取学习互动数据
  async LoadMyStudyData() {
    this.setReviewList([
      {
        id: 1,
        type: 'homework',
        reviewed: true,
        reviewerId: '"1112029144067084319"',
        reviewerNickname: '暄桐助教',
        content: '连续练习三天，手感越来越好了。连续练习三天，手感越来越好了。连续练习三天，手感越来越好了。',
        tag: '第2课《三坟记》篆隶进阶',
        cover: 'https://testing-file.xuantong.cn/images/origin/2af4793d0f2b4e308bf0cd8dbb645ced.jpg',
        receiveTime: 1774330844000
      }
    ])
    this.setStudyActionData({
      homework: 32,
      rewards: 32,
      daily: 32,
      collect: 32
    })
  }

  // 获取用户面板数据
  async fetchUserPanelData() {
    this.setUserPanelData({
      follow: 1,
      followed: 2,
      like: 3
    })
  }

  // 获取我的学习数据统计
  async fetchStudyRecordData(memberId: string) {
    try {
      const [err, res] = await $service<UserStatisticsResponse>({
        url: EnumApiUser.UserStatistics,
        data: {
          id: memberId
        }
      })

      if (err) throw err

      const { learnTimeCounts, learnTimeWeek, registeredTime, sequentialLearnDays, memberWeekRecordMap } = res

      let lastWeekLearnData = (memberWeekRecordMap ? Object.keys(memberWeekRecordMap) : []).sort().map(key => {
        return {
          date: +key,
          time: +Math.floor(memberWeekRecordMap[key] / 60)
        }
      })

      this.setStudyRecordData({
        learnTime: Math.round(((learnTimeCounts ?? 0) / 3600) * 10) / 10,
        weekLearnTime: Math.round((learnTimeWeek ?? 0) / 3600),
        learnStartTime: registeredTime,
        learnEndTime: moment(registeredTime)
          .add(sequentialLearnDays ?? 0, 'days')
          .valueOf(),
        continueDays: sequentialLearnDays ?? 0,
        lastWeekLearnData
      })
    } catch (error) {
      reportEntryLog('classroom.loading-study-record-data.fail', { error })
    }
  }
  // 获取我的课程列表
  async LoadMyProductList() {
    try {
      this.setMyProductStatus({
        loading: true
      })

      const [err, res] = await $service<MyCourseList>({
        url: EnumApiMy.MyCourseList
      })

      if (err) throw err

      this.setMyProductList([...transfromProductList(res)])

      this.setMyProductStatus({
        haveNoneCourse: this.myProductList.length === 0,
        isError: false
      })
    } catch (error) {
      reportEntryLog('toclass.loading-data.fail', { error })
      this.setMyProductStatus({
        isError: true
      })
    } finally {
      this.setMyProductStatus({
        loading: false
      })
    }
  }

  @action
  setAllClasshourList(
    list: Array<
      ICalendarData & {
        y: number
        m: number
        d: number
      }
    >
  ) {
    this.allClasshourList = [...list]
  }

  @action
  setCalendarTimeData(list: Array<CalendarTimeData>) {
    this.calendarTimeData = [...list]
  }

  @action
  setStudyActionData(data: MyClassroomType['StudyAction']) {
    this.studyActionData = { ...data }
  }

  @action
  setReviewList(list: Array<StudyReviewType>) {
    this.reviewList = [...list]
  }

  @action
  setMyProductList(res: Array<CourseItem & CourseCardUIType>) {
    this.myProductList = [...res]
  }

  @action
  setMyProductStatus(status: Partial<MyProductStatus>) {
    this.myProductStatus = {
      ...this.myProductStatus,
      ...status
    }
  }

  @action
  setUserPanelData(data: MyClassroomType['UserPanel']) {
    this.userPanelData = {
      ...data
    }
  }

  @action
  setStudyRecordData(data: MyClassroomType['StudyRecord']) {
    this.studyRecordData = {
      ...data
    }
  }

  @action
  reset() {
    this.userPanelData = {
      follow: 0,
      followed: 0,
      like: 0
    }
    this.studyRecordData = null
    this.myProductList = []
    this.myProductStatus = {
      loading: true,
      haveNoneCourse: false,
      isError: false
    }
    this.reviewList = []
    this.studyActionData = {
      homework: 0,
      rewards: 0,
      daily: 0,
      collect: 0
    }
    this.calendarTimeData = []
    this.allClasshourList = []
  }
}
